/**
 * Environment variable validation for Frontend
 *
 * This module validates required environment variables at runtime startup.
 * All VITE_ prefixed variables are available via import.meta.env
 */

interface EnvConfig {
  /** API base URL for backend services */
  VITE_API_URL?: string;
  /** WebSocket URL for real-time updates */
  VITE_WS_URL?: string;
  /** Current environment */
  MODE: string;
  /** Is production build */
  PROD: boolean;
  /** Is development mode */
  DEV: boolean;
}

interface ValidationResult {
  isValid: boolean;
  missingVars: string[];
  warnings: string[];
}

/**
 * Required environment variables that must be set
 */
const REQUIRED_VARS: Array<keyof EnvConfig> = [
  // Add required variables here when needed
  // 'VITE_API_URL',
];

/**
 * Optional variables that trigger warnings if not set in production
 */
const RECOMMENDED_VARS: Array<keyof EnvConfig> = [
  'VITE_API_URL',
];

/**
 * Validate environment variables
 */
export function validateEnv(): ValidationResult {
  const env = import.meta.env as EnvConfig;
  const missingVars: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  for (const varName of REQUIRED_VARS) {
    const value = env[varName];
    if (value === undefined || value === '') {
      missingVars.push(varName);
    }
  }

  // Check recommended variables in production
  if (env.PROD) {
    for (const varName of RECOMMENDED_VARS) {
      const value = env[varName];
      if (value === undefined || value === '') {
        warnings.push(`${varName} is not set (recommended for production)`);
      }
    }
  }

  return {
    isValid: missingVars.length === 0,
    missingVars,
    warnings,
  };
}

/**
 * Print environment validation errors to console
 */
export function printEnvErrors(result: ValidationResult): void {
  if (result.missingVars.length > 0 || result.warnings.length > 0) {
    console.group('%c🔧 Frontend Environment Configuration', 'color: #ff6b6b; font-weight: bold;');

    if (result.missingVars.length > 0) {
      console.error('Missing required environment variables:');
      result.missingVars.forEach(v => console.error(`  - ${v}`));
      console.log('');
      console.log('Please set these variables in your .env or .env.local file:');
      console.log('  VITE_API_URL=http://localhost:3001');
    }

    if (result.warnings.length > 0) {
      console.warn('Configuration warnings:');
      result.warnings.forEach(w => console.warn(`  ⚠️ ${w}`));
    }

    console.groupEnd();
  }
}

/**
 * Get validated environment configuration
 */
export function getEnvConfig(): EnvConfig {
  const result = validateEnv();

  // Print errors/warnings in development
  if (import.meta.env.DEV) {
    printEnvErrors(result);
  }

  // In production, fail hard if required vars are missing
  if (import.meta.env.PROD && !result.isValid) {
    printEnvErrors(result);
    throw new Error(
      `Frontend configuration error: Missing required environment variables: ${result.missingVars.join(', ')}`
    );
  }

  return import.meta.env as EnvConfig;
}

/**
 * Environment configuration singleton
 * Import this in your application to get validated env vars
 */
export const env = getEnvConfig();

/**
 * Helper to get API URL with fallback
 */
export const API_URL = env.VITE_API_URL || '/api';

/**
 * Helper to check if we're in development mode
 */
export const isDev = env.DEV;

/**
 * Helper to check if we're in production mode
 */
export const isProd = env.PROD;
